import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Popover, Transition } from '@headlessui/react';
import { T } from '@tolgee/react';
import { useLanguage } from '../../../../lib/tolgee/tolgee-utils';
import { OptimizedImage } from '../../../../components/ui/OptimizedImage';


// Tipos para nuestras estructuras de datos
type Brand = {
  name: string;
  nameKey?: string;
  image: string;
  href: string;
  description: string;
  descriptionKey?: string;
}

// Definición de marcas (actualizado según BrandsFeature)
const Brands: Brand[] = [
  {
    name: "PREMIfood",
    nameKey: "brands.brand_names.premifood",
    image: "/images/brands/jarad-premifood.webp",
    href: "/brands/premifood",
    description: "Alimento premium para aves y roedores. Productos 100% naturales, ideales tanto para competición como para quienes desean ofrecer a sus aves y roedores una alimentación premium que garantice su salud y rendimiento.",
    descriptionKey: "brands.brand_descriptions.premifood"
  },
  {
    name: "Jarad La Mejor Selección",
    nameKey: "brands.brand_names.mejor_seleccion",
    image: "/images/brands/jarad-mejor-seleccion.webp",
    href: "/brands/la-mejor-seleccion",
    description: "Alimentación especializada para criadores y competidores. Diseñada para expertos dedicados a la cría y competición intensiva de roedores, ofreciendo fórmulas especializadas que aseguran un óptimo desarrollo y condición física.",
    descriptionKey: "brands.brand_descriptions.mejor_seleccion"
  },
  {
    name: "Active Menú",
    nameKey: "brands.brand_names.active_menu",
    image: "/images/brands/jarad-active.webp",
    href: "/brands/active-menu",
    description: "Alimentación completa y equilibrada para aves y roedores. Formulados específicamente para cubrir todas las necesidades nutricionales diarias, garantizando salud y vitalidad.",
    descriptionKey: "brands.brand_descriptions.active_menu"
  },
  {
    name: "Diet Complet",
    nameKey: "brands.brand_names.diet_complet",
    image: "/images/brands/jarad-diet.webp",
    href: "/brands/diet-complet",
    description: "Alimento extrusionado innovador para aves. Elaborado con materias primas seleccionadas, en formato extrusionado y porciones pequeñas, para una alimentación práctica y nutritiva.",
    descriptionKey: "brands.brand_descriptions.diet_complet"
  },
  {
    name: "Zoo Campero",
    nameKey: "brands.brand_names.zoo_campero",
    image: "/images/brands/jarad-zoo-campero.webp",
    href: "/brands/zoo-campero",
    description: "Piensos granulados completos para animales de granja. Proporcionan una dieta rica y equilibrada, contribuyendo al bienestar y productividad de los animales.",
    descriptionKey: "brands.brand_descriptions.zoo_campero"
  },
  {
    name: "Jarad Snacks",
    nameKey: "brands.brand_names.jarad_snacks",
    image: "/images/brands/jarad-snack.webp",
    href: "/brands/jarad-snacks",
    description: "Snacks naturales llenos de sabor y nutrientes para aves y roedores. Fuente de energía y nutrientes adicionales gracias a ingredientes naturales como miel, huevo o fruta.",
    descriptionKey: "brands.brand_descriptions.jarad_snacks"
  },
  {
    name: "Jarad Vital",
    nameKey: "brands.brand_names.vital",
    image: "/images/brands/jarad-vital.webp",
    href: "/brands/vital",
    description: "Alimentación natural para perros y gatos. Nutrición fiable y equilibrada, cubriendo todas las necesidades básicas de las mascotas.",
    descriptionKey: "brands.brand_descriptions.vital"
  },
  {
    name: "Jarad Plus",
    nameKey: "brands.brand_names.plus",
    image: "/images/brands/jarad-plus.webp",
    href: "/brands/plus",
    description: "Alimento premium para perros y gatos. Desarrollado con los mejores ingredientes naturales para satisfacer integralmente las necesidades nutricionales.",
    descriptionKey: "brands.brand_descriptions.plus"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const tabs = [
  { id: "categories", labelKey: "sidebar.categories", label: "Categorías" },
  { id: "brands", labelKey: "sidebar.brands", label: "Marcas" }
];

const SideMenu = () => {
  const [activeTab, setActiveTab] = useState("categories");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { language, changeLanguage } = useLanguage();

  // Categorías hardcodeadas (nombres en mayúsculas para coincidir con Meilisearch)
  const categoryHierarchy = [
    {
      id: 'aves',
      name: 'AVES',
      slug: 'aves',
      image: 'https://cdn.jaradpetfood.com/Aves.png',
      children: [
        { id: 'aves-alimentacion', name: 'ALIMENTACIÓN', slug: 'aves-alimentacion', parent: 'aves' },
        { id: 'aves-accesorios', name: 'ACCESORIOS', slug: 'aves-accesorios', parent: 'aves' },
        { id: 'aves-complementos', name: 'COMPLEMENTOS ALIMENTICIOS', slug: 'aves-complementos-alimenticios', parent: 'aves' },
        { id: 'aves-snacks', name: 'SNACKS', slug: 'aves-snacks', parent: 'aves' }
      ]
    },
    {
      id: 'roedores',
      name: 'ROEDORES',
      slug: 'roedores',
      image: 'https://cdn.jaradpetfood.com/Roedores.png',
      children: [
        { id: 'roedores-alimentacion', name: 'ALIMENTACIÓN', slug: 'roedores-alimentacion', parent: 'roedores' },
        { id: 'roedores-accesorios', name: 'ACCESORIOS', slug: 'roedores-accesorios', parent: 'roedores' },
        { id: 'roedores-snacks', name: 'SNACKS', slug: 'roedores-snacks', parent: 'roedores' }
      ]
    },
    {
      id: 'perros',
      name: 'PERROS',
      slug: 'perros',
      image: 'https://cdn.jaradpetfood.com/Perros.png',
      children: [
        { id: 'perros-alimentacion-humeda', name: 'ALIMENTACIÓN HÚMEDA', slug: 'perros-alimentacion-humeda', parent: 'perros' },
        { id: 'perros-alimentacion-seca', name: 'ALIMENTACIÓN SECA', slug: 'perros-alimentacion-seca', parent: 'perros' },
        { id: 'perros-snacks', name: 'SNACKS', slug: 'perros-snacks', parent: 'perros' },
        { id: 'perros-higiene', name: 'HIGIENE', slug: 'perros-higiene', parent: 'perros' },
        { id: 'perros-accesorios', name: 'ACCESORIOS', slug: 'perros-accesorios', parent: 'perros' }
      ]
    },
    {
      id: 'gatos',
      name: 'GATOS',
      slug: 'gatos',
      image: 'https://cdn.jaradpetfood.com/Gatos.png',
      children: [
        { id: 'gatos-alimentacion-humeda', name: 'ALIMENTACIÓN HÚMEDA', slug: 'gatos-alimentacion-humeda', parent: 'gatos' },
        { id: 'gatos-alimentacion-seca', name: 'ALIMENTACIÓN SECA', slug: 'gatos-alimentacion-seca', parent: 'gatos' },
        { id: 'gatos-arenas', name: 'ARENAS ABSORVENTES', slug: 'gatos-arenas', parent: 'gatos' },
        { id: 'gatos-accesorios', name: 'ACCESORIOS', slug: 'gatos-accesorios', parent: 'gatos' },
        { id: 'gatos-snacks', name: 'SNACKS', slug: 'gatos-snacks', parent: 'gatos' }
      ]
    },
    {
      id: 'peces',
      name: 'PECES',
      slug: 'peces',
      image: 'https://cdn.jaradpetfood.com/Peces.png',
      children: [
        { id: 'peces-alimentacion', name: 'ALIMENTACIÓN', slug: 'peces-alimentacion', parent: 'peces' },
        { id: 'peces-accesorios', name: 'ACCESORIOS', slug: 'peces-accesorios', parent: 'peces' }
      ]
    },
    {
      id: 'tortugas',
      name: 'TORTUGAS',
      slug: 'tortugas',
      image: 'https://cdn.jaradpetfood.com/Tortugas.png',
      children: [
        { id: 'tortugas-alimentacion', name: 'ALIMENTACIÓN', slug: 'tortugas-alimentacion', parent: 'tortugas' }
      ]
    }
  ];

  const loadingCategories = false;

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId === selectedCategory ? null : categoryId);
  };

  const handleLanguageChange = (newLanguage: string) => {
    changeLanguage(newLanguage);
  };

  return (
    <div className="h-full" data-testid="side-menu">
      <div className="flex items-center h-full">
        <Popover className="h-full">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  className="relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 px-2"
                  style={{ color: '#0d0d0d' }}
                  data-testid="menu-button"
                >
                  <div className="flex flex-col justify-center items-center w-6 h-6 space-y-1 group">
                    <span className="w-full h-0.5 bg-current transform transition-all duration-300 group-hover:rotate-[8deg] group-hover:w-5/6"></span>
                    <span className="w-full h-0.5 bg-current transition-all duration-300 group-hover:w-full"></span>
                    <span className="w-full h-0.5 bg-current transform transition-all duration-300 group-hover:rotate-[-8deg] group-hover:w-5/6"></span>
                  </div>
                </Popover.Button>
              </div>

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 -translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-2"
              >
                <Popover.Panel className="fixed left-0 w-screen top-16 z-30 bg-black bg-opacity-95 backdrop-blur-sm shadow-xl max-h-[100vh] overflow-hidden">
                  <div className="flex flex-col w-full h-full">
                    {/* Pestañas de navegación */}
                    <div className="flex items-center justify-between border-b border-gray-800 bg-black w-full px-4 py-2">
                      {/* Selector de idioma - Lado izquierdo */}
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-400 text-sm hidden sm:block">
                          <T keyName="common.language">Idioma</T>
                        </span>
                        <div className="relative">
                          <select
                            value={language}
                            onChange={(e) => handleLanguageChange(e.target.value)}
                            className="appearance-none bg-gray-900 rounded-lg px-3 py-2 text-sm font-medium text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent cursor-pointer pr-8 min-w-[80px]"
                          >
                            <option value="es" className="bg-gray-900 text-white">
                              🇪🇸 <span className="hidden sm:inline">Español</span>
                            </option>
                            <option value="en" className="bg-gray-900 text-white">
                              🇺🇸 <span className="hidden sm:inline">English</span>
                            </option>
                            <option value="ar" className="bg-gray-900 text-white">
                              🇸🇦 <span className="hidden sm:inline">العربية</span>
                            </option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Pestañas centradas */}
                      <div className="flex space-x-1">
                        {tabs.map((tab) => (
                          <button
                            key={tab.id}
                            className={`px-3 sm:px-6 py-2 rounded-lg transition-all font-medium text-sm sm:text-base ${activeTab === tab.id
                              ? "bg-gray-800 text-white"
                              : "text-gray-400 hover:text-white hover:bg-gray-900"
                              }`}
                            onClick={() => setActiveTab(tab.id)}
                          >
                            <T keyName={tab.labelKey}>{tab.label}</T>
                          </button>
                        ))}
                      </div>

                      {/* Espacio vacío para mantener el centrado en desktop */}
                      <div className="hidden sm:block w-32"></div>
                    </div>

                    {/* Contenido principal - ahora con scroll */}
                    <div className="overflow-y-auto max-h-[calc(100vh-120px)] w-full">
                      <div className="py-12 w-full">
                        {activeTab === "categories" ? (
                          <div className="w-full px-4 mx-auto" style={{ maxWidth: "1920px" }}>
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                              {/* Lista de categorías - Formato móvil (pills) en dispositivos pequeños */}
                              <div className="md:col-span-1 md:border-r md:border-gray-800">
                                <div className="pr-0 md:pr-4">
                                  <h3 className="text-white text-xl font-medium mb-4">
                                    <T keyName="sidebar.categories">Categorías</T>
                                  </h3>

                                  {/* Vista móvil - Pills */}
                                  <div className="flex flex-wrap gap-2 mb-6 md:hidden">
                                    {loadingCategories ? (
                                      <div className="text-gray-400 text-sm"><T keyName="sidebar.loading_categories">Cargando categorías...</T></div>
                                    ) : (
                                      categoryHierarchy.map((category) => (
                                        <button
                                          key={category.id}
                                          className={`flex items-center py-1 px-3 rounded-full transition-all ${selectedCategory === category.id
                                            ? "bg-gray-800 text-white border-2 border-gray-700"
                                            : "bg-gray-900 text-gray-400 border border-gray-800"
                                            }`}
                                          onClick={() => handleCategorySelect(category.id)}
                                        >
                                          <span>{category.name}</span>
                                        </button>
                                      ))
                                    )}

                                    {/* Enlaces adicionales en móvil */}
                                    <div className="flex flex-wrap gap-2 mt-4 w-full">
                                      <Link
                                        to="/catalog"
                                        onClick={close}
                                        className="flex items-center py-1.5 px-4 rounded-full transition-all text-black font-semibold"
                                        style={{ backgroundColor: '#f6cf02' }}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                        <span><T keyName="header.catalog">Catálogo</T></span>
                                      </Link>

                                      <Link
                                        to="/categories"
                                        onClick={close}
                                        className="flex items-center py-1 px-3 rounded-full transition-all bg-gray-900 text-white border border-gray-700"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        </svg>
                                        <span><T keyName="sidebar.view_all_categories">Todas las categorías</T></span>
                                      </Link>
                                    </div>

                                    {/* Pedido rápido IA en móvil */}
                                    <div className="flex flex-wrap gap-2 mt-3 w-full">
                                      <Link
                                        to="/quick-order"
                                        onClick={close}
                                        className="flex items-center gap-1.5 py-1.5 px-4 rounded-full transition-all text-black font-semibold"
                                        style={{ backgroundColor: '#f6cf02', animation: 'ai-glow 3s ease-in-out infinite' }}
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        <span>Pedido rápido</span>
                                      </Link>
                                    </div>

                                    {/* Enlaces de empresa en móvil */}
                                    <div className="flex flex-wrap gap-2 mt-3 w-full">
                                      <Link
                                        to="/distribuidores"
                                        onClick={close}
                                        className="flex items-center py-1 px-3 rounded-full transition-all bg-gray-900 text-white border border-gray-700"
                                      >
                                        <span><T keyName="header.distributors">Distribuidores</T></span>
                                      </Link>
                                      <Link
                                        to="/about-us"
                                        onClick={close}
                                        className="flex items-center py-1 px-3 rounded-full transition-all bg-gray-900 text-white border border-gray-700"
                                      >
                                        <span><T keyName="header.about_us">Sobre nosotros</T></span>
                                      </Link>
                                      <Link
                                        to="/blog"
                                        onClick={close}
                                        className="flex items-center py-1 px-3 rounded-full transition-all bg-gray-900 text-white border border-gray-700"
                                      >
                                        <span><T keyName="header.blog">Blog</T></span>
                                      </Link>
                                      <Link
                                        to="/contact"
                                        onClick={close}
                                        className="flex items-center py-1 px-3 rounded-full transition-all bg-gray-900 text-white border border-gray-700"
                                      >
                                        <span><T keyName="header.contact">Contacto</T></span>
                                      </Link>
                                    </div>
                                  </div>

                                  {/* Vista desktop - Lista vertical */}
                                  <div className="hidden md:block space-y-2">
                                    {loadingCategories ? (
                                      <div className="text-gray-400 text-sm"><T keyName="sidebar.loading_categories">Cargando categorías...</T></div>
                                    ) : (
                                      categoryHierarchy.map((category) => (
                                        <button
                                          key={category.id}
                                          className={`flex items-center w-full py-2 px-3 rounded-lg text-left transition-all ${selectedCategory === category.id
                                            ? "bg-gray-800 text-white"
                                            : "hover:bg-gray-900 text-gray-400 hover:text-white"
                                            }`}
                                          onClick={() => handleCategorySelect(category.id)}
                                        >
                                          <span>{category.name}</span>
                                        </button>
                                      ))
                                    )}

                                    {/* Enlace al catálogo */}
                                    <Link
                                      to="/catalog"
                                      onClick={close}
                                      className="flex items-center w-full py-2.5 px-3 mt-4 rounded-lg text-left transition-all font-semibold text-black hover:opacity-90"
                                      style={{ backgroundColor: '#f6cf02' }}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                      </svg>
                                      <span><T keyName="header.catalog">Catálogo</T></span>
                                    </Link>

                                    {/* Nuevo enlace para ver todas las categorías */}
                                    <Link
                                      to="/categories"
                                      onClick={close}
                                      className="flex items-center w-full py-2 px-3 mt-2 rounded-lg text-left transition-all bg-black/40 hover:bg-black/60 text-white border border-white/20"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                      </svg>
                                      <span><T keyName="sidebar.view_all_categories">Ver todas las categorías</T></span>
                                    </Link>

                                    {/* Enlaces de empresa */}
                                    <div className="mt-6 pt-4 border-t border-gray-800">
                                      <h3 className="text-white text-lg font-medium mb-3">
                                        <T keyName="header.company">Empresa</T>
                                      </h3>
                                      <Link
                                        to="/distribuidores"
                                        onClick={close}
                                        className="flex items-center w-full py-2 px-3 rounded-lg text-left transition-all hover:bg-gray-900 text-gray-400 hover:text-white"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        <span><T keyName="header.distributors">Distribuidores</T></span>
                                      </Link>
                                      <Link
                                        to="/about-us"
                                        onClick={close}
                                        className="flex items-center w-full py-2 px-3 rounded-lg text-left transition-all hover:bg-gray-900 text-gray-400 hover:text-white"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span><T keyName="header.about_us">Sobre nosotros</T></span>
                                      </Link>
                                      <Link
                                        to="/blog"
                                        onClick={close}
                                        className="flex items-center w-full py-2 px-3 rounded-lg text-left transition-all hover:bg-gray-900 text-gray-400 hover:text-white"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                        </svg>
                                        <span><T keyName="header.blog">Blog</T></span>
                                      </Link>
                                      <Link
                                        to="/contact"
                                        onClick={close}
                                        className="flex items-center w-full py-2 px-3 rounded-lg text-left transition-all hover:bg-gray-900 text-gray-400 hover:text-white"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <span><T keyName="header.contact">Contacto</T></span>
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Contenido de la categoría seleccionada o todas las categorías */}
                              <div className="md:col-span-4">
                                {selectedCategory ? (
                                  (() => {
                                    const category = categoryHierarchy.find(cat => cat.id === selectedCategory);
                                    if (!category) return null;

                                    return (
                                      <motion.div
                                        key={selectedCategory}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="grid grid-cols-1 gap-8"
                                      >
                                        {/* Información de categoría */}
                                        <div>
                                          <h3 className="text-2xl font-medium mb-5 pb-2 border-b border-gray-800 text-white">
                                            {category.name}
                                          </h3>

                                          {/* Subcategorías */}
                                          {category.children && category.children.length > 0 && (
                                            <div className="mt-6">
                                              <h4 className="text-lg font-medium text-white mb-4"><T keyName="sidebar.subcategories">Subcategorías</T></h4>
                                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {category.children.map(subcategory => {
                                                  // Construir categoryPath jerárquico: "Padre > Hijo"
                                                  const categoryPath = `${category.name} > ${subcategory.name}`;
                                                  return (
                                                    <Link
                                                      key={subcategory.id}
                                                      to={`/products?page=1&category=${encodeURIComponent(categoryPath)}`}
                                                      onClick={close}
                                                      className="group p-4 bg-gray-900 rounded-lg hover:bg-gray-800 transition-all duration-300"
                                                    >
                                                      <h4 className="text-white font-medium group-hover:text-gray-300 transition-colors flex items-center">
                                                        {subcategory.name}
                                                        <span className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                                                      </h4>
                                                    </Link>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}

                                          {/* Link a ver todos los productos de esta categoría */}
                                          <div className="mt-6">
                                            <Link
                                              to={`/products?page=1&category=${encodeURIComponent(category.name)}`}
                                              onClick={close}
                                              className="inline-block px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-200 transition-all font-medium"
                                            >
                                              <T keyName="sidebar.view_all_products_of" params={{ category: category.name }} />
                                            </Link>
                                          </div>
                                        </div>
                                      </motion.div>
                                    );
                                  })()
                                ) : (
                                  <motion.div
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                                  >
                                    {loadingCategories ? (
                                      <div className="col-span-full text-center text-gray-400 py-12">
                                        <div className="animate-spin h-12 w-12 border-2 border-gray-600 border-t-white rounded-full mx-auto mb-4"></div>
                                        <p><T keyName="sidebar.loading_categories">Cargando categorías...</T></p>
                                      </div>
                                    ) : categoryHierarchy.length === 0 ? (
                                      <div className="col-span-full text-center text-gray-400 py-12">
                                        <p><T keyName="sidebar.no_categories_available">No hay categorías disponibles</T></p>
                                      </div>
                                    ) : (
                                      categoryHierarchy.map((category) => (
                                        <motion.div
                                          key={category.id}
                                          variants={itemVariants}
                                          className="flex flex-col group relative cursor-pointer"
                                          onClick={() => handleCategorySelect(category.id)}
                                        >
                                          <div className="relative w-full aspect-square mb-4 overflow-hidden rounded-xl p-6 bg-gray-800 flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-gray-700">
                                            {category.image ? (
                                              <OptimizedImage
                                                src={category.image}
                                                alt={category.name}
                                                width={200}
                                                height={200}
                                                type="product"
                                                className="w-full h-full object-contain"
                                                onerror="redirect"
                                              />
                                            ) : (
                                              <div className="text-6xl">{category.name.charAt(0)}</div>
                                            )}
                                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                                              <h3 className="text-white text-xl font-medium">{category.name}</h3>
                                              {category.children && category.children.length > 0 && (
                                                <p className="text-gray-300 text-sm mt-1">
                                                  {category.children.length} {category.children.length === 1 ? 'subcategoría' : 'subcategorías'}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))
                                    )}
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                            style={{ maxWidth: "1920px" }}
                          >
                            {Brands.map((brand) => (
                              <motion.div
                                key={brand.name}
                                variants={itemVariants}
                              >
                                <Link
                                  to={`/products?page=1&brand=${encodeURIComponent(brand.name)}`}
                                  onClick={close}
                                  className="flex flex-col group cursor-pointer"
                                >
                                  <div className="relative w-full aspect-video mb-4 overflow-hidden rounded-xl bg-gray-100 p-4 flex items-center justify-center group-hover:bg-gray-200 transition-all duration-300">
                                    <OptimizedImage
                                      src={brand.image}
                                      alt={brand.name}
                                      width={300}
                                      height={200}
                                      type="product"
                                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                    />
                                  </div>
                                  <h3 className="text-white text-xl font-medium group-hover:text-gray-300 transition-colors">
                                    <T keyName={brand.nameKey || `brands.brand_names.${brand.name.toLowerCase().replace(/\s+/g, '_')}`}>{brand.name}</T>
                                  </h3>
                                  <p className="text-gray-400 text-sm mt-1">
                                    <T keyName={brand.descriptionKey || `brands.brand_descriptions.${brand.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}`}>{brand.description}</T>
                                  </p>
                                </Link>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  );
};

export default SideMenu; 